import { useEffect, useMemo, useState } from "react";
import { X, Copy, QrCode } from "lucide-react";
import { toast } from "sonner";

/**
 * FavoritesQRModal — shows a QR code for sharing the favorites list.
 * Only the *codes* are encoded (compact + robust): the importing device
 * looks up artist/title from /api/music-list.
 */
export default function FavoritesQRModal({ open, favs, onClose }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const shareUrl = useMemo(() => {
    if (!favs) return "";
    const codes = favs.map((f) => f.code).filter(Boolean);
    if (codes.length === 0) return "";
    const origin = (typeof window !== "undefined" && window.location?.origin) || "https://www.rumorezero.com";
    return `${origin}/?fav=${codes.join(",")}`;
  }, [favs]);

  const missingCount = favs ? favs.filter((f) => !f.code).length : 0;
  const includedCount = favs ? favs.filter((f) => f.code).length : 0;

  if (!open) return null;

  const qrSrc = shareUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=8&data=${encodeURIComponent(shareUrl)}`
    : null;

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <div
      data-testid="favorites-qr-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-[#332f2b] bg-[#12100e] p-4 sm:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 w-8 h-8 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/40 flex items-center justify-center text-[#FF6B00]">
              <QrCode size={16} />
            </span>
            <div className="min-w-0">
              <h3 className="font-anton uppercase text-lg tracking-wide text-[#F5F2EB] truncate">
                Porta i preferiti ovunque
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#69635c] mt-0.5">
                Scansiona con un altro dispositivo
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="favorites-qr-close"
            onClick={onClose}
            aria-label="Chiudi"
            className="shrink-0 p-2 rounded-md border border-[#332f2b] text-[#A39A8F] hover:text-[#F5F2EB] hover:border-[#FF6B00]/50 transition"
          >
            <X size={16} />
          </button>
        </div>

        {shareUrl ? (
          <>
            <div className="rounded-xl overflow-hidden border border-[#332f2b] bg-white p-4 flex items-center justify-center">
              <img
                data-testid="favorites-qr-image"
                src={qrSrc}
                alt="QR con la lista dei preferiti"
                className="w-full max-w-[320px] h-auto"
              />
            </div>

            <div className="mt-3 font-mono text-[10px] text-[#A39A8F] text-center">
              {includedCount} {includedCount === 1 ? "brano incluso" : "brani inclusi"}
              {missingCount > 0 && (
                <> · <span className="text-[#E1A140]">{missingCount} senza codice esclusi</span></>
              )}
            </div>

            <div className="mt-3 flex items-stretch gap-2">
              <input
                data-testid="favorites-qr-url"
                readOnly
                value={shareUrl}
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-[#332f2b] bg-[#1a1816] text-[#A39A8F] font-mono text-[11px] focus:outline-none focus:border-[#FF6B00]/50"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                data-testid="favorites-qr-copy"
                onClick={copyLink}
                className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#FF6B00]/50 bg-[#FF6B00]/10 text-[#FF6B00] hover:bg-[#FF6B00]/20 transition font-mono text-[10px] uppercase tracking-[0.18em]"
              >
                <Copy size={13} />
                {copied ? "Copiato" : "Copia"}
              </button>
            </div>
          </>
        ) : (
          <div className="rounded-xl border border-[#332f2b] bg-[#1a1816] p-6 text-center">
            <p className="font-mono text-xs text-[#A39A8F]">
              Nessun preferito con codice da condividere. Aggiungi qualche brano dalla lista Richieste per condividerlo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
